import { ContentBlockProps } from 'src/components/ContentBlock/ContentBlock';
import { StorageContentBlockProps } from './types';

const textAlignMap = {
  start: 'left',
  center: 'center',
  end: 'right',
} as const;

/**
 * Converts a storage content block to a content block with the appropriate properties.
 *
 * @param props - The properties of the storage content block.
 * @returns The content block properties.
 *
 * The function extracts the title, subtitle, body, and behaviors from the storage content block properties.
 * It then maps these properties to the corresponding content block properties, including theme, text alignment,
 * and heading elements and styles.
 *
 * @example
 * ```typescript
 * const storageContentBlock = {
 *   title: "Sample Title",
 *   subtitle: "Sample Subtitle",
 *   body: { processed: "<p>Sample Body</p>" },
 *   behaviors: {
 *     entity_variant: { theme: "dark", text_align: "start" },
 *     entity_heading: { element: "h1", style: "typography_h1" },
 *     entity_subheading: { element: "h2", style: "typography_h2" }
 *   }
 * };
 *
 * const contentBlock = fromStorageToContentBlock(storageContentBlock);
 * console.log(contentBlock);
 * // Output:
 * // {
 * //   title: { element: "h1", className: "custom_h1", children: "Sample Title" },
 * //   subtitle: { element: "h2", className: "custom_h2", children: "Sample Subtitle" },
 * //   body: "Sample Body",
 * //   theme: "dark",
 * //   textAlign: "center"
 * // }
 * ```
 */
export const fromStorageToContentBlock = (
  props: StorageContentBlockProps['node'],
): ContentBlockProps => {
  const { title: storageTitle, subtitle: storageSubtitle, body: storageBody, behaviors } = props;
  const theme = behaviors?.entity_variant?.theme;
  const textAlign = textAlignMap[behaviors?.entity_variant?.text_align ?? 'start'];
  const title = {
    element: behaviors?.entity_heading?.element ?? 'h2',
    className: behaviors?.entity_heading?.style ?? 'typography_h2',
    children: storageTitle,
  };

  const subtitle = storageSubtitle
    ? {
        element: behaviors?.entity_subheading?.element ?? 'h3',
        className: behaviors?.entity_subheading?.style ?? 'typography_h3',
        children: storageSubtitle,
      }
    : undefined;

  const body = storageBody?.processed
    ? {
        children: storageBody.processed,
        className: '',
      }
    : undefined;

  return {
    title,
    subtitle,
    body,
    theme,
    textAlign,
  };
};
